import { SelectedStartType } from "../../config/enums";
import { ClearCache } from "../../api/recordService";
import { convertUTCDateTimeToLocal, toDisplayDateTime } from "../../blocks/fields/formatDate";
import { lookupRecordsCache } from "../../hooks/lookupRecords";
import { PageRecord, PageSaveOption, SaveConfirmation } from "../recordDefinition.types";
import { isPublishedABTest } from "./abtestRules";

// What the lookups were read from, as `lookupRecordsCache` holds them.
const nameOf = (cacheKey: string, id: unknown, fallback: unknown) =>
  lookupRecordsCache[cacheKey]?.find((option) => String(option.Key) === String(id))
    ?.Value ??
  fallback ??
  "";

const attributes = [
  "g4c_name",
  "g4c_description",
  "g4c_communicationaid",
  "g4c_communicationbid",
  "g4c_audienceid",
  "g4c_testpercentage",
  "g4c_testperiod",
  "g4c_metric",
  "g4c_winnertype",
  "g4c_starttype",
  "g4c_startdate",
  "g4c_enddate",
  "g4c_expiryurl",
];

/** The service stores the names of the records the test points at beside their ids. */
export function toABTestPayload(record: PageRecord): PageRecord {
  return {
    ...Object.fromEntries(attributes.map((name) => [name, record[name]])),
    // The service reads the save intent from `saveAction`.
    saveAction: record.action ?? "save",
    g4c_communicationaidname: nameOf(
      "g4c_communication:abtest",
      record.g4c_communicationaid,
      record.g4c_communicationaidname
    ),
    g4c_communicationbidname: nameOf(
      "g4c_communication:abtest",
      record.g4c_communicationbid,
      record.g4c_communicationbidname
    ),
    g4c_audienceidname: nameOf(
      "g4c_communicationaudience",
      record.g4c_audienceid,
      record.g4c_audienceidname
    ),
  };
}

// Publishing to an audience with nobody in it, or one not yet counted, is refused by the service.
const audienceReason = (record: PageRecord) =>
  record.audienceCounting
    ? "The audience is still being counted"
    : Number(record.finalCount) > 0
    ? undefined
    : "The audience needs a count above 0 before this can be published";

/** Save and Publish is its own button beside Save. */
export const abtestSaveMenu: PageSaveOption[] = [
  {
    id: "publish",
    separate: true,
    label: "Save and Publish",
    patch: () => ({ action: "saveAndPublish" }),
    visible: (record) => !isPublishedABTest(record),
    disabledReason: audienceReason,
  },
];

/** What publishing says it will do, by when the test starts. */
export function abtestSaveConfirmation(record: PageRecord): SaveConfirmation | null {
  if (record.action !== "saveAndPublish") {
    return null;
  }

  const showDate = (value: unknown) =>
    value ? toDisplayDateTime(convertUTCDateTimeToLocal(String(value))?.toDate()) || String(value) : "";

  if (String(record.g4c_starttype) === String(SelectedStartType.Scheduled)) {
    return {
      title: "Publish Scheduled A/B Test",
      confirmText: "Schedule Publish",
      paragraphs: [
        `Do you want to publish this A/B Test? This action will attempt to schedule the publishing of this A/B Test from ${showDate(record.g4c_startdate)} to ${showDate(record.g4c_enddate)}.`,
      ],
    };
  }

  return {
    title: "Publish A/B Test Immediately",
    confirmText: "Publish Immediately",
    paragraphs: [
      "Do you want to publish this A/B Test? This action will attempt to publish this A/B Test immediately.",
    ],
  };
}

/** Publishing changes the communications' status, so the lists of them are read again. */
export function afterABTestSave(record: PageRecord) {
  if (record.action === "saveAndPublish") {
    ClearCache("g4c_communication");
  }
}
